import { Salad, Sprout, ChefHat, Package, Clock, Smartphone } from "lucide-react";
import { Section } from "@/components/Section";
import { useHomepageContent } from "@/hooks/useHomepageContent";
import { HOMEPAGE_FALLBACKS } from "@/lib/homepage-defaults";
import { useImageFallback } from "@/hooks/useImageFallback";

const ITEMS = [
  { icon: Salad, title: "Nutrition-first meals", body: "Every combo is curated for balance — protein, fibre, and macro-conscious portions." },
  { icon: Sprout, title: "Farm-direct ingredients", body: "Raw materials sourced directly from farmers we know and work with." },
  { icon: ChefHat, title: "Separate Veg & Non-Veg kitchens", body: "Fully separated prep spaces — no cross-contact, ever." },
  { icon: Package, title: "Food-grade packaging", body: "Hygienic, food-safe containers built for fresh delivery across Mumbai." },
  { icon: Clock, title: "Freshly prepared meals", body: "Cooked to slot — never reheated batches sitting around." },
  { icon: Smartphone, title: "Tech-enabled ordering", body: "Simple order-ahead flow with clear cutoffs for every meal slot." },
];

export function WhyChoose() {
  const content = useHomepageContent();
  const banner = useImageFallback(HOMEPAGE_FALLBACKS.whyChooseBannerUrl);
  return (
    <Section>
      <div className="max-w-2xl">
        <div className="mb-3 h-px w-10 bg-primary/40" aria-hidden />
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Why Healthy Kitchen</span>
        <h2 className="mt-3 font-display text-4xl font-medium md:text-5xl">{content.whyChooseHeading}</h2>
        {content.whyChooseDescription ? (
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            {content.whyChooseDescription}
          </p>
        ) : null}
      </div>
      <div className="mt-10 overflow-hidden rounded-3xl ring-1 ring-border">
        <img
          src={content.whyChooseBannerUrl}
          alt="Chef carefully plating a healthy Indian meal in a bright modern kitchen."
          width={1920}
          height={820}
          loading="lazy"
          decoding="async"
          onError={banner.onError}
          className="aspect-[16/10] h-full w-full object-cover md:aspect-[21/9]"
        />
      </div>
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
        {ITEMS.map(({ icon: Icon, title, body }) => (
          <div
            key={title}
            className="group rounded-2xl border border-border bg-card p-7 transition-all duration-300 ease-out hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md motion-reduce:transform-none motion-reduce:transition-none"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-secondary to-background text-primary ring-1 ring-border transition-colors group-hover:ring-primary/30">
              <Icon size={22} strokeWidth={1.75} aria-hidden />
            </div>
            <h3 className="mt-6 font-display text-xl font-medium tracking-tight">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}